import { useBlogQueries } from '@/hooks/queries/useBlogQueries';
import { liftOnHover, slideInFromBottom, staggerContainer, staggerItem, viewportOptions } from '@/lib/animations';
import { BlogPost } from '@/types/blog';
import { Link } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { memo, useRef, useEffect } from 'react';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader } from '../ui/card';


// Blog card component for carousel
const BlogCard = memo(({ post }: { post: BlogPost }) => {
  return (
    <Link to='/blog/$slug' params={{ slug: post.slug }}>
      <motion.div className="w-80 md:w-96 flex-shrink-0"
        variants={liftOnHover}
        initial="rest"
        whileHover="hover">
        <Card className="border-0 h-full">
          <CardHeader className="min-h-[120px] p-4 md:p-6">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              {post.tags.map((tag: string) => (
                <Badge key={tag} variant="outline" className="text-xs">
                  {tag}
                </Badge>
              ))}
            </div>
            <h3 className="text-lg md:text-xl font-bold text-black hover:text-brand transition-colors min-h-[48px] line-clamp-2">
              {post.title}
            </h3>
          </CardHeader>
          <CardContent className="p-0">
            <div className="flex flex-col h-full">
              {post.image && (
                <div className="h-48 flex-shrink-0">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div className="p-4 md:p-6 flex flex-col justify-between min-h-[160px]">
                <div className="flex-1">
                  <p className="text-gray-700 text-sm md:text-base mb-4 line-clamp-3 min-h-[60px]">
                    {post.description}
                  </p>
                </div>
                <div className="flex justify-between items-end mt-auto">
                  <div>
                    <p className="text-sm font-medium text-gray-700">By {post.author}</p>
                  </div>
                  <div className="text-brand text-sm font-medium">
                    Read More →
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </Link>
  );
});
BlogCard.displayName = 'BlogCard';


export default function BlogSlider() {
  const { useFetchBlogPosts } = useBlogQueries();
  const { data: posts = [] } = useFetchBlogPosts(10);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<Animation | null>(null);
  const cardsWrapperRef = useRef<HTMLDivElement>(null);

  // Duplicate posts for seamless infinite scroll
  const allPosts = posts.length > 0 ? [...posts, ...posts] : [];

  // Animation pause state
  const isPausedRef = useRef(false);
  const isDraggingRef = useRef(false);
  // Timer to pause animation after drag
  const dragPauseTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!scrollContainerRef.current || !cardsWrapperRef.current || posts.length < 2) return;

    const container = scrollContainerRef.current;
    const cardsWrapper = cardsWrapperRef.current;

    // Reset scroll position
    container.scrollLeft = 0;

    // Calculate total scroll distance (width of one set of cards)
    const cardSetWidth = cardsWrapper.scrollWidth / 2;

    // Clean up any previous animation
    if (animationRef.current) {
      animationRef.current.cancel();
      animationRef.current = null;
    }

    // Slower duration (80 seconds)
    const duration = 80000;
    let rafId: number | null = null;
    let start: number | null = null;

    function step(ts: number) {
      if (!start) start = ts;
      if (!isPausedRef.current && !isDraggingRef.current) {
        const elapsed = ts - start;
        const progress = (elapsed % duration) / duration;
        container.scrollLeft = cardSetWidth * progress;
      } else {
        // If paused, reset start so animation resumes smoothly
        start = ts - ((container.scrollLeft / cardSetWidth) * duration);
      }
      rafId = requestAnimationFrame(step);
    }
    rafId = requestAnimationFrame(step);
    animationRef.current = {
      cancel: () => {
        if (rafId) cancelAnimationFrame(rafId);
      }
    } as unknown as Animation;

    // Reset scroll position on mouse enter/leave for seamless loop
    const handleScroll = () => {
      if (container.scrollLeft >= cardSetWidth) {
        container.scrollLeft = 0;
      }
    };
    container.addEventListener('scroll', handleScroll);

    return () => {
      if (animationRef.current) {
        animationRef.current.cancel();
        animationRef.current = null;
      }
      container.removeEventListener('scroll', handleScroll);
      if (dragPauseTimeoutRef.current) {
        clearTimeout(dragPauseTimeoutRef.current);
        dragPauseTimeoutRef.current = null;
      }
    };
  }, [posts.length]);

  if (!posts.length) return null;

  return (
    <section>
      {/* Header */}
      <motion.div
        className="text-center mb-12"
        variants={staggerContainer}
        initial="hidden"
        whileInView="visible"
        viewport={viewportOptions}
      >
        <motion.span className="landing-badge" variants={staggerItem}>
          Blog
        </motion.span>
        <motion.h2 className="landing-title" variants={staggerItem}>
          Latest Insights
        </motion.h2>
        <motion.p className="landing-subtitle" variants={staggerItem}>
          Learn about the latest trends in real estate and winning strategies for today's home sellers
        </motion.p>
      </motion.div>

      {/* Auto-scrolling Carousel Container */}
      <div className="relative overflow-hidden -mx-8">
        <motion.div
          className="relative"
          variants={slideInFromBottom}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOptions}
        >
          {/* Scrollable Blog Cards Container */}
          <div
            ref={scrollContainerRef}
            className="flex overflow-x-auto scrollbar-hide pb-4 gap-6 px-8"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            onMouseEnter={() => { isPausedRef.current = true; }}
            onMouseLeave={() => { isPausedRef.current = false; }}
            onMouseDown={() => { isDraggingRef.current = true; }}
            onMouseUp={() => {
              isDraggingRef.current = false;
              // Pause animation for 1.5s after drag
              isPausedRef.current = true;
              if (dragPauseTimeoutRef.current) clearTimeout(dragPauseTimeoutRef.current);
              dragPauseTimeoutRef.current = setTimeout(() => {
                isPausedRef.current = false;
                dragPauseTimeoutRef.current = null;
              }, 1500);
            }}
            onTouchStart={() => { isDraggingRef.current = true; }}
            onTouchEnd={() => {
              isDraggingRef.current = false;
              // Pause animation for 1.5s after drag
              isPausedRef.current = true;
              if (dragPauseTimeoutRef.current) clearTimeout(dragPauseTimeoutRef.current);
              dragPauseTimeoutRef.current = setTimeout(() => {
                isPausedRef.current = false;
                dragPauseTimeoutRef.current = null;
              }, 1500);
            }}
          >
            <div ref={cardsWrapperRef} className="flex gap-6">
              {allPosts.map((post, idx) => (
                <BlogCard key={post.slug + '-' + idx} post={post} />
              ))}
            </div>
          </div>

          {/* Fade Gradients */}
          <div className="hidden md:block absolute left-0 top-0 w-20 h-full bg-gradient-to-r from-background via-background/70 to-transparent pointer-events-none z-10" />
          <div className="hidden md:block absolute right-0 top-0 w-20 h-full bg-gradient-to-l from-background via-background/70 to-transparent pointer-events-none z-10" />
        </motion.div>
      </div>

      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </section>
  );
}